import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map, of, switchMap } from 'rxjs';
import { APP_PATHS } from '@core/constants';
import { Service } from '@core/models';
import { ServiceStore } from '@root/app/store/service.store';
import { LocationStore } from '@root/app/store/location.store';
import { PageHeaderComponent, PageHeaderBadge } from '@custom-components/layout/page-header/page-header.component';
import { PageBodyComponent } from '@custom-components';
import { CardComponent } from '@custom-components/general/card';
import { CardGridComponent } from '@custom-components/general/card-grid';
import { DetailFieldComponent } from '@custom-components/general/detail-field';
import { BreadcrumbItem } from '@custom-components/general/breadcrumb/breadcrumb.component';
import { StatusBadgeType } from '@custom-components/general/status-chip/status-chip.component';

const STATUS_BADGE_MAP: Record<string, StatusBadgeType> = {
  'Active': 'success',
  'Pending activation': 'warning',
  'Terminated': 'error',
  'Pending termination': 'warning',
};

@Component({
  selector: 'xemex-service-detail-page',
  standalone: true,
  providers: [LocationStore],
  imports: [CommonModule, RouterModule, TranslateModule, PageHeaderComponent, PageBodyComponent, CardComponent, CardGridComponent, DetailFieldComponent],
  templateUrl: './service-detail-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceDetailPageComponent {
  readonly serviceStore = inject(ServiceStore);
  readonly locationStore = inject(LocationStore);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  readonly service = computed(() => this.serviceStore.selectedService?.());
  readonly locationDetail = this.locationStore.locationDetail;

  readonly packageName = toSignal(
    toObservable(computed(() => this.serviceStore.selectedService?.())).pipe(
      switchMap(s =>
        s?.package
          ? s.package.pipe(map(p => p?.packageDescription ?? p?.packageCode ?? ''))
          : of(''),
      ),
    ),
    { initialValue: '' },
  );

  readonly breadcrumbs = computed<BreadcrumbItem[]>(() => [
    { label: this.translate.instant('sidebarMenu.home'), routerLink: '/' },
    { label: this.translate.instant('sidebarMenu.services'), routerLink: `/${APP_PATHS.Services}` },
    { label: this.packageName() || '—' },
  ]);

  readonly statusBadges = computed<PageHeaderBadge[]>(() => {
    const status = this.service()?.status;
    if (!status) return [];
    return [{ label: status, status: STATUS_BADGE_MAP[status] ?? 'inactive' }];
  });

  readonly formattedPrice = computed(() => {
    const price = this.service()?.priceInCents;
    return price != null ? `€${(price / 100).toFixed(2)}` : '—';
  });

  readonly locationStreet = computed(() => {
    const loc = this.locationDetail();
    if (!loc?.locationStreet) return '—';
    const parts = [loc.locationStreet, loc.locationStreetNumber];
    if (loc.locationStreetNumberAddition) parts.push(loc.locationStreetNumberAddition);
    return parts.join(' ');
  });

  protected onLocationMore(): void {
    const route = this.locationDetailRoute();
    if (route) this.router.navigate([route]);
  }

  readonly locationDetailRoute = computed(() => {
    const locationId = this.service()?.locationId;
    return locationId ? `/${APP_PATHS.LocationDetail.replace(':locationId', locationId.toString())}` : null;
  });

  constructor() {
    const stored = this.serviceStore.selectedService?.();
    if (!stored) {
      const historyService = (history as History & { state: { service?: Service } }).state?.service;
      if (historyService) {
        this.serviceStore.setSelectedService(historyService);
      } else {
        this.router.navigate([`/${APP_PATHS.Services}`]);
        return;
      }
    }

    const service = this.serviceStore.selectedService?.();
    if (service?.locationId) {
      this.locationStore.getLocationDetailMethod(service.locationId.toString());
    }
  }
}
